// src/App.js
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from "react";

import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";

import ModelViewer from "./ModelViewer";
import Header from "./components/Header";
import AnswersCard from "./components/AnswersCard";
import CluesSection from "./components/CluesSection";
import DescriptionFrame from "./components/DescriptionFrame";
import GameOverScreen from "./components/GameOverScreen";

import "./App.css";

import countryDistances from "./country_distances.json";
import countryData from "./Centre.json";
import objectsData from "./objectsData.json";

import {
  findNearestCorrectCountryInfo,
  calculatePercentage
} from "./utils/distanceUtils";

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────
const MAX_GUESSES = 6;
const MAX_ROUNDS = 3;

// Score multipliers: index = (# guesses used)
// e.g. if user guesses correctly on first try, they earn 5 points, etc.
const SCORE_MULTIPLIERS = [5, 4, 3, 2, 1, 0];

/**
 * CountryOriginGame
 * ──────────────────────────────────────────────────────────────────────
 * The main game component that handles:
 *   - Picking a random 3D object each round
 *   - Tracking guesses, distance feedback, and score
 *   - Displaying the 3D model (via <ModelViewer />)
 *   - Showing guess input, clues, and previous guess cards
 *   - At end of round, revealing the answer + showing social icons + bottom sheet
 *   - At game over, displaying <GameOverScreen />
 */
export default function CountryOriginGame() {
  // ───────────────────────────────────────────────────────
  // State Hooks
  // ───────────────────────────────────────────────────────
  const [currentObject, setCurrentObject] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [round, setRound] = useState(0);

  const [guess, setGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [guessesLeft, setGuessesLeft] = useState(MAX_GUESSES);
  const [correctCountry, setCorrectCountry] = useState(false);

  const [usedIndices, setUsedIndices] = useState([]);
  const [feedbackSlots, setFeedbackSlots] = useState(Array(MAX_GUESSES).fill(null));

  const [visibleClues, setVisibleClues] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorTimeout, setErrorTimeout] = useState(null);

  const [allRoundsData, setAllRoundsData] = useState([]);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [manualCameraMovement, setManualCameraMovement] = useState(false);

  const [isFirstRound, setIsFirstRound] = useState(true);

  const cardRef = useRef(null);

  // ───────────────────────────────────────────────────────
  // Memoized list of "available" object indices (not yet used)
  // ───────────────────────────────────────────────────────
  const availableIndices = useMemo(() => {
    if (!objectsData || !Array.isArray(objectsData)) return [];
    return objectsData
      .map((_, idx) => idx)
      .filter((idx) => !usedIndices.includes(idx));
  }, [usedIndices]);

  // ───────────────────────────────────────────────────────
  // showError(message): display a toast‐style error for 3 seconds
  // ───────────────────────────────────────────────────────
  const showError = useCallback(
    (message) => {
      if (errorTimeout) clearTimeout(errorTimeout);
      setErrorMessage(message);
      const to = setTimeout(() => setErrorMessage(null), 3000);
      setErrorTimeout(to);
      return () => clearTimeout(to);
    },
    [errorTimeout]
  );

  // ───────────────────────────────────────────────────────
  // Scroll the card into view whenever feedbackSlots or correctCountry changes
  // ───────────────────────────────────────────────────────
  useEffect(() => {
    if (cardRef.current && (feedbackSlots.some((s) => s !== null) || correctCountry)) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [feedbackSlots, correctCountry]);

  // ───────────────────────────────────────────────────────
  // Whenever `round` changes: either start a new round or end the game
  // ───────────────────────────────────────────────────────
  useEffect(() => {
    if (round < MAX_ROUNDS) {
      try {
        startNewRound();
      } catch (err) {
        console.error("Error starting new round:", err);
        showError("Error loading game data. Check console for details.");
      }
    } else {
      setGameOver(true);
    }
    return () => {
      if (errorTimeout) clearTimeout(errorTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  // ───────────────────────────────────────────────────────
  // Whenever `guessesLeft` or `currentObject` changes, unlock clues
  // ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentObject?.clues) return;

    const newVisible = [];
    if (guessesLeft <= 3 && currentObject.clues[0]) {
      newVisible.push(currentObject.clues[0]);
    }
    if (guessesLeft <= 2 && currentObject.clues[1]) {
      newVisible.push(currentObject.clues[1]);
    }
    if (guessesLeft <= 1 && currentObject.clues[2]) {
      newVisible.push(currentObject.clues[2]);
    }
    setVisibleClues(newVisible);
  }, [guessesLeft, currentObject]);

  // ───────────────────────────────────────────────────────
  // startNewRound(): pick a random new object, reset state
  // ───────────────────────────────────────────────────────
  const startNewRound = useCallback(() => {
    if (!objectsData?.length) {
      console.error("objectsData missing or invalid:", objectsData);
      showError("Error: Unable to load object data.");
      return;
    }
    if (availableIndices.length === 0) {
      setGameOver(true);
      return;
    }

    // Pick a random index from those not yet used
    const randIdx = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    const obj = {
      ...objectsData[randIdx],
      // If no explicit Wikipedia URL, build one from the name
      wikipediaUrl:
        objectsData[randIdx].wikipediaUrl ||
        `https://en.wikipedia.org/wiki/${encodeURIComponent(objectsData[randIdx].name)}`,
    };

    // Validate: must have a nonempty countries array
    if (!obj.countries || !Array.isArray(obj.countries) || obj.countries.length === 0) {
      console.error("Selected object missing a valid `countries` array:", obj);
      showError("Error: Object missing country data.");
      return;
    }

    // Reset per‐round state:
    setCurrentObject(obj);
    setGuessesLeft(MAX_GUESSES);
    setCorrectCountry(false);
    setFeedbackSlots(Array(MAX_GUESSES).fill(null));
    setDescriptionExpanded(false);
    
    // Set isFirstRound to false after the first round
    if (isFirstRound) {
      setIsFirstRound(false);
    }
  }, [availableIndices, showError, isFirstRound]);

  // ───────────────────────────────────────────────────────
  // handleInputChange: track user's input, show country suggestions
  // ───────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const val = e.target.value;
    setGuess(val);

    if (val.length > 0) {
      const filtered = countryData
        .map((c) => c.name)
        .filter((name) => name.toLowerCase().startsWith(val.toLowerCase()))
        .slice(0, 10);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  // ───────────────────────────────────────────────────────
  // handleGuess: user pressed "Submit." Check correctness or give feedback.
  // ───────────────────────────────────────────────────────
  const handleGuess = () => {
    const trimmed = guess.trim().toLowerCase();
    if (trimmed === "") {
      showError("Please enter a country");
      return;
    }
    const guessedCountry = countryData.find((c) => c.name.toLowerCase() === trimmed);
    if (!guessedCountry) {
      showError("Country not found");
      return;
    }

    // Check if correct
    const isCorrect = currentObject.countries.some((ct) => ct.toLowerCase() === trimmed);
    if (isCorrect) {
      handleCorrectGuess(guessedCountry);
    } else {
      handleIncorrectGuess(guessedCountry);
    }

    // Clear input & suggestions
    setGuess("");
    setSuggestions([]);
  };

  // ───────────────────────────────────────────────────────
  // handleCorrectGuess: user got it right
  // ───────────────────────────────────────────────────────
  const handleCorrectGuess = (guessedCountry) => {
    if (round >= MAX_ROUNDS) return;

    // Determine points from the array, based on how many guesses used
    const points = SCORE_MULTIPLIERS[MAX_GUESSES - guessesLeft];
    setScore((prev) => prev + points);
    setCorrectCountry(true);

    // Mark this object as "used"
    setUsedIndices((prev) => [...prev, objectsData.findIndex((o) => o.id === currentObject.id)]);

    // Fill in feedbackSlots
    const updated = [...feedbackSlots];
    const nextIdx = updated.findIndex((slot) => slot === null);

    const info = {
      guess: guessedCountry.name,
      distance: 0,
      bearing: 0,
      percentage: 100,
      isCorrect: true,
    };

    if (nextIdx !== -1) {
      updated[nextIdx] = info;

      // Save this round's data (before auto‐filling remaining slots)
      setAllRoundsData((prev) => [
        ...prev,
        { object: currentObject, feedbackSlots: [...updated], score: points },
      ]);

      // Auto‐fill any remaining slots as "correct" for display
      for (let i = nextIdx + 1; i < MAX_GUESSES; i++) {
        updated[i] = { ...info, guess: "Auto-filled" };
      }
      setFeedbackSlots(updated);
    }
  };

  // ───────────────────────────────────────────────────────
  // handleIncorrectGuess: user guessed incorrectly
  // ───────────────────────────────────────────────────────
  const handleIncorrectGuess = (guessedCountry) => {
    if (round >= MAX_ROUNDS) return;

    const newLeft = guessesLeft - 1;
    setGuessesLeft(newLeft);

    // Compute distance, bearing, percentage to nearest correct country
    const { distance, bearing } = findNearestCorrectCountryInfo(
      guessedCountry.name,
      currentObject.countries,
      countryDistances
    );
    const percentage = calculatePercentage(distance);

    const guessInfo = {
      guess: guessedCountry.name,
      distance: distance != null ? Math.round(distance) : null,
      bearing,
      percentage,
      isCorrect: false,
      nearestCorrectCountry: null,
    };

    const updated = [...feedbackSlots];
    const nextIdx = updated.findIndex((slot) => slot === null);
    if (nextIdx !== -1) {
      updated[nextIdx] = guessInfo;
      setFeedbackSlots(updated);
    }

    // If out of guesses, end the round
    if (newLeft === 0) {
      setCorrectCountry(true);
      setAllRoundsData((prev) => [
        ...prev,
        { object: currentObject, feedbackSlots: updated, score: 0 },
      ]);
    }
  };

  // ───────────────────────────────────────────────────────
  // handleNext: move to the next round (or finish if none left)
  // ───────────────────────────────────────────────────────
  const handleNext = () => {
    setRound((r) => r + 1);
  };

  // ───────────────────────────────────────────────────────
  // handleSuggestionClick: user clicked a country suggestion
  // ───────────────────────────────────────────────────────
  const handleSuggestionClick = (s) => {
    setGuess(s.toUpperCase());
    setSuggestions([]);
  };

  // ───────────────────────────────────────────────────────
  // restartGame: reset all state and begin again
  // ───────────────────────────────────────────────────────
  const restartGame = () => {
    setScore(0);
    setRound(0);
    setGameOver(false);
    setGuess("");
    setSuggestions([]);
    setGuessesLeft(MAX_GUESSES);
    setUsedIndices([]);
    setFeedbackSlots(Array(MAX_GUESSES).fill(null));
    setAllRoundsData([]);
    setCorrectCountry(false);
    setDescriptionExpanded(false);
  };

  // ───────────────────────────────────────────────────────
  // If gameOver, render <GameOverScreen /> and return early
  // ───────────────────────────────────────────────────────
  if (gameOver) {
    return (
      <GameOverScreen
        score={score}
        allRoundsData={allRoundsData}
        restartGame={restartGame}
      />
    );
  }

  const roundIsOver = correctCountry || guessesLeft === 0;

  return (
    <div>
      {/* Header: progress bars + "FRESCO" + icons */}
      <Header round={round} />

      <Card className="w-96" ref={cardRef}>
        <CardContent>
          {currentObject && (
            <>
              {/* ─────────────────────────────────────────── */}
              {/* MODEL VIEWER (3D model display) */}
              {/* ─────────────────────────────────────────── */}
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#000000",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  width: "80%",
                  height: "300px",
                }}
              >
                <ModelViewer
                  key={currentObject.id}
                  url={currentObject.glbUrl}
                  guessesLeft={guessesLeft}
                  enableControls={true}
                  autoRotate={!manualCameraMovement}
                  onManualCameraMove={() => setManualCameraMovement(true)}
                  correctGuess={correctCountry}
                  isFirstRound={isFirstRound}
                />
              </div>

              {/* Error toast (shown for 3 seconds) */}
              {errorMessage && <div className="error-toast">{errorMessage}</div>}

              {/* ─────────────────────────────────────────── */}
              {/* GUESSING SCREEN (only if the round is NOT over) */}
              {/* ─────────────────────────────────────────── */}
              {!roundIsOver && (
                <>
                  <div
                    className="input-button-row"
                    style={{
                      display: "flex",
                      gap: "8px",
                      width: "100%",
                      marginBottom: "3px",
                      alignItems: "center",
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <Input
                      type="text"
                      value={
                        correctCountry || guessesLeft === 0
                          ? currentObject.countries.join(", ").toUpperCase()
                          : guess
                      }
                      onChange={handleInputChange}
                      placeholder={
                        correctCountry || guessesLeft === 0
                          ? currentObject.countries.join(", ").toUpperCase()
                          : "Type country name..."
                      }
                      disabled={correctCountry || guessesLeft === 0}
                      style={{
                        height: "40px",
                        fontSize: "1rem",
                        padding: "0 10px",
                        backgroundColor: correctCountry ? "#7b7b7b" : "inherit",
                        color: correctCountry || guessesLeft === 0 ? "#fff" : "inherit",
                      }}
                    />

                    {correctCountry || guessesLeft === 0 ? (
                      <Button onClick={handleNext} style={{ fontSize: "1rem" }}>
                        Next
                      </Button>
                    ) : (
                      <Button onClick={handleGuess} style={{ fontSize: "1rem" }}>
                        Submit
                      </Button>
                    )}

                    {suggestions.length > 0 && (
                      <ul className="suggestions">
                        {suggestions.map((s, i) => (
                          <li key={i} onClick={() => handleSuggestionClick(s)}>
                            {s.toUpperCase()}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Clues that appear as guessesLeft decreases */}
                  <CluesSection visibleClues={visibleClues} />

                  {/* List of previous guesses with distances/arrows/percentages */}
                  <AnswersCard slots={feedbackSlots} />
                </>
              )}

              {/* ─────────────────────────────────────────── */}
              {/* REVEAL SCREEN (once roundIsOver = true) */}
              {/* ─────────────────────────────────────────── */}
              {roundIsOver && (
                <>
                  {/* Object Name + Country + Year */}
                  <div className="reveal-header">
                    <h2 className="object-name">{currentObject.name}</h2>
                    <p className="object-info">
                      {currentObject.countries.join(", ")},{" "}
                      {currentObject.year > 0
                        ? `${currentObject.year}`
                        : `${Math.abs(currentObject.year)} BC`}
                    </p>
                  </div>

                  {/* Social Buttons: Wikipedia, Share, Like, + Next */}
                  <div className="social-row">
                    {/* Wikipedia icon ⇒ open new tab */}
                    <button
                      className="social-btn"
                      onClick={() => window.open(currentObject.wikipediaUrl, "_blank")}
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/6/63/Wikipedia-logo.png"
                        alt="Wiki"
                        className="social-icon"
                      />
                    </button>

                    {/* Share icon ⇒ navigator.share fallback */}
                    <button
                      className="social-btn"
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: currentObject.name,
                            url: currentObject.wikipediaUrl,
                          });
                        } else {
                          window.open(currentObject.wikipediaUrl, "_blank");
                        }
                      }}
                    >
                      <span className="icon">🔗</span>
                    </button>

                    {/* Like icon (no action attached in this example) */}
                    <button className="social-btn">
                      <span className="icon">❤️</span>
                    </button>

                    {/* NEXT button ⇒ next round */}
                    <button className="btn next-btn" onClick={handleNext}>
                      NEXT
                    </button>
                  </div>

                  {/* Bottom‐sheet with object description (peek/expand) */}
                  <DescriptionFrame
                    object={currentObject}
                    expanded={descriptionExpanded}
                    setExpanded={setDescriptionExpanded}
                  />
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
